/**
 * Who is signed in, and what the app does about it.
 *
 * Three decisions are encoded here, and each one is a rule from the brief rather
 * than a preference:
 *
 *   **A cold start never blocks.** Resolving the session is a network round trip,
 *   and the writing surface does not need its answer. Children render on the
 *   first frame; `restoring` is true underneath while the answer arrives. A
 *   learner who opens the app to write a chord does not wait for an auth check
 *   they did not ask for.
 *
 *   **Signed out is a mode, not a failure.** Guest use is first class. Nothing in
 *   this file redirects a signed-out learner anywhere, and no screen is gated on
 *   `status === 'signed-in'` except the account screens themselves.
 *
 *   **Unreachable is not the same as signed out.** If the server cannot be
 *   reached the honest answer is "I don't know yet", so `reachable` records that
 *   separately instead of quietly showing a Sign in button to somebody who is
 *   already signed in and merely offline.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiClient } from '../api/client';
import { AuthClient, SIGNED_OUT, type AuthState } from '../api/auth';

export interface AuthContextValue {
  state: AuthState;
  /** True until the first session check settles. The app renders regardless. */
  restoring: boolean;
  /** False when the last attempt could not reach the server at all. */
  reachable: boolean;
  /**
   * Set when a session the app believed was live turned out to be gone. Distinct
   * from being signed out: it means work in progress may not have reached the
   * server, so it is announced rather than silently applied.
   */
  sessionExpired: boolean;
  dismissExpiry: () => void;
  /** Re-ask the server. Used after actions that change the session elsewhere. */
  refresh: () => Promise<void>;
  auth: AuthClient;
  api: ApiClient;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /**
   * Test seam, and deliberately at the `fetch` boundary rather than the client
   * boundary. Injecting whole clients would mean tests exercise a stub instead
   * of the real state mapping — the part most worth testing. Substituting
   * `fetch` leaves every line of `AuthClient` and `ApiClient` under test and
   * replaces only the network.
   */
  fetchImpl?: typeof fetch;
}

export function AuthProvider({ children, fetchImpl }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(SIGNED_OUT);
  const [restoring, setRestoring] = useState(true);
  const [reachable, setReachable] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Read inside callbacks that must not re-run when the value changes.
  const statusRef = useRef(state.status);
  statusRef.current = state.status;

  const api = useMemo(
    () =>
      new ApiClient({
        fetchImpl,
        onSessionExpired: () => {
          // Only a surprise is worth announcing. A learner who was already
          // signed out is not "expired", and a 401 while signed out is the
          // normal shape of guest use.
          if (statusRef.current === 'signed-in') setSessionExpired(true);
          setState(SIGNED_OUT);
        },
      }),
    [fetchImpl],
  );

  const authClient = useMemo(
    () => new AuthClient({ fetchImpl, onStateChange: setState }),
    [fetchImpl],
  );

  const refresh = useCallback(async () => {
    try {
      await authClient.currentState();
      setReachable(true);
    } catch {
      // Offline, a timeout or a server fault all leave the previous answer
      // standing. Guessing "signed out" here would sign a learner out of their
      // own account every time a train went into a tunnel.
      setReachable(false);
    } finally {
      setRestoring(false);
    }
  }, [authClient]);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      // CSRF first: Django issues the token from its own endpoint, and without
      // it every later mutation is refused with a 403 that reads like a
      // permissions bug. It is also harmless when signed out.
      try {
        await api.primeCsrf();
      } catch {
        /* An unreachable server is reported by the session check below. */
      }
      if (cancelled) return;
      await refresh();
    })();

    return () => {
      cancelled = true;
    };
  }, [api, refresh]);

  // Coming back online is the moment to find out what is actually true.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onOnline = () => void refresh();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      restoring,
      reachable,
      sessionExpired,
      dismissExpiry: () => setSessionExpired(false),
      refresh,
      auth: authClient,
      api,
    }),
    [state, restoring, reachable, sessionExpired, refresh, authClient, api],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside an AuthProvider.');
  return value;
}
